import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Mail, Lock, User, Building2, Globe, Eye, EyeOff, ArrowRight, ArrowLeft, CheckCircle2, Ticket, KeyRound, MessageCircle, Bot, Zap, Users, TrendingUp } from 'lucide-react'

import api from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { resolvePostLoginDestination } from '@/lib/verticalUI'
import { getWhatsAppSalesUrl } from '@/utils/contactLinks'

// Claves para localStorage
const STORAGE_KEYS = {
  LOGIN_EMAIL: 'login_form_email',
  SIGNUP_NAME: 'login_form_name',
  SIGNUP_COUNTRY: 'login_form_country',
  SIGNUP_COMPANY: 'login_form_company',
  SIGNUP_INVITE_CODE: 'login_form_invite_code',
}

// Helpers seguros para almacenamiento local compatibles con SSR / Node tests
const safeGetStorage = (key: string): string => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem(key) || ''
    }
  } catch {
    // Entorno restringido o sin permisos
  }
  return ''
}

const safeSetStorage = (key: string, value: string): void => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(key, value)
    }
  } catch {
    // Entorno restringido o sin permisos
  }
}

const safeRemoveStorage = (key: string): void => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(key)
    }
  } catch {
    // Entorno restringido o sin permisos
  }
}


// Países de Latinoamérica
const COUNTRIES = [
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'BO', name: 'Bolivia', flag: '🇧🇴' },
  { code: 'BR', name: 'Brasil', flag: '🇧🇷' },
  { code: 'CL', name: 'Chile', flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴' },
  { code: 'CR', name: 'Costa Rica', flag: '🇨🇷' },
  { code: 'CU', name: 'Cuba', flag: '🇨🇺' },
  { code: 'DO', name: 'República Dominicana', flag: '🇩🇴' },
  { code: 'EC', name: 'Ecuador', flag: '🇪🇨' },
  { code: 'SV', name: 'El Salvador', flag: '🇸🇻' },
  { code: 'GT', name: 'Guatemala', flag: '🇬🇹' },
  { code: 'HN', name: 'Honduras', flag: '🇭🇳' },
  { code: 'MX', name: 'México', flag: '🇲🇽' },
  { code: 'NI', name: 'Nicaragua', flag: '🇳🇮' },
  { code: 'PA', name: 'Panamá', flag: '🇵🇦' },
  { code: 'PY', name: 'Paraguay', flag: '🇵🇾' },
  { code: 'PE', name: 'Perú', flag: '🇵🇪' },
  { code: 'PR', name: 'Puerto Rico', flag: '🇵🇷' },
  { code: 'UY', name: 'Uruguay', flag: '🇺🇾' },
  { code: 'VE', name: 'Venezuela', flag: '🇻🇪' },
  { code: 'ES', name: 'España', flag: '🇪🇸' },
  { code: 'US', name: 'Estados Unidos', flag: '🇺🇸' },
  { code: 'OTHER', name: 'Otro', flag: '🌍' },
]

export function validateSignupInput(fields: {
  email: string
  password: string
  name: string
  inviteCode: string
}): { valid: boolean; error?: string } {
  if (!fields.email || !fields.email.includes('@')) {
    return { valid: false, error: 'Por favor ingresa un correo electrónico válido.' }
  }
  if (!fields.name.trim()) {
    return { valid: false, error: 'Por favor ingresa tu nombre completo.' }
  }
  if (!fields.password || fields.password.length < 6) {
    return { valid: false, error: 'La contraseña debe tener al menos 6 caracteres.' }
  }
  if (!fields.inviteCode || !fields.inviteCode.trim()) {
    return {
      valid: false,
      error: 'Debes ingresar un código de invitación válido para registrarte en la Beta privada.',
    }
  }
  return { valid: true }
}

export interface LoginPageProps {
  initialMode?: 'login' | 'signup'
}

export default function LoginPage({ initialMode }: LoginPageProps = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const login = useAuthStore((s) => s.login)

  const searchParams = new URLSearchParams(location.search)
  const queryMode = searchParams.get('mode')
  const queryInvite = searchParams.get('invite')
  const queryPlan = searchParams.get('plan')

  // Limpiar cualquier residuo de login_form_mode previo
  useEffect(() => {
    try {
      safeRemoveStorage('login_form_mode')
    } catch {
      // Ignorar restricciones en entornos aislados
    }
  }, [])

  // Derivar modo directamente de la URL (pathname y query params) para que sean páginas separadas
  const isSignupPath = location.pathname === '/register' || location.pathname === '/registro'
  const isLoginPath = location.pathname === '/login'

  const isLogin = isSignupPath
    ? false
    : isLoginPath
    ? queryMode !== 'signup'
    : initialMode === 'signup'
    ? false
    : queryMode !== 'signup'

  const [email, setEmail] = useState(() => safeGetStorage(STORAGE_KEYS.LOGIN_EMAIL) || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [name, setName] = useState(() => safeGetStorage(STORAGE_KEYS.SIGNUP_NAME) || '')
  const [company, setCompany] = useState(() => safeGetStorage(STORAGE_KEYS.SIGNUP_COMPANY) || '')
  const [country, setCountry] = useState(() => safeGetStorage(STORAGE_KEYS.SIGNUP_COUNTRY) || '')
  const [inviteCode, setInviteCode] = useState(() => queryInvite || safeGetStorage(STORAGE_KEYS.SIGNUP_INVITE_CODE) || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Limpiar error al cambiar de ruta/modo
  useEffect(() => {
    setError('')
  }, [isLogin])

  // Guardar datos en localStorage cuando cambien
  useEffect(() => {
    if (email) {
      safeSetStorage(STORAGE_KEYS.LOGIN_EMAIL, email)
    }
  }, [email])

  useEffect(() => {
    if (name && !isLogin) {
      safeSetStorage(STORAGE_KEYS.SIGNUP_NAME, name)
    }
  }, [name, isLogin])

  useEffect(() => {
    if (inviteCode && !isLogin) {
      safeSetStorage(STORAGE_KEYS.SIGNUP_INVITE_CODE, inviteCode)
    }
  }, [inviteCode, isLogin])

  // Construir URLs para alternar limpiamente entre login y registro preservando plan e invitación
  const buildTargetUrl = (targetMode: 'login' | 'signup') => {
    const params = new URLSearchParams(location.search)
    params.delete('mode')
    const queryString = params.toString() ? `?${params.toString()}` : ''
    return targetMode === 'login' ? `/login${queryString}` : `/register${queryString}`
  }

  // Función para obtener mensaje de error más descriptivo
  const getErrorMessage = (err: any): string => {
    // Error de red/conexión
    if (!err.response) {
      // Error local/controlado (ej: validación de respuesta)
      if (typeof err?.message === 'string' && err.message.trim()) {
        const msg = err.message.trim()
        const isNetworkLike =
          err.code === 'ECONNABORTED' ||
          msg.includes('timeout') ||
          msg.includes('Network Error') ||
          msg.includes('Failed to fetch')
        if (!isNetworkLike) return msg
      }
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        return 'El servidor tardó demasiado en responder. Por favor, verifica tu conexión a internet e intenta nuevamente.'
      }
      if (err.message?.includes('Network Error') || err.message?.includes('Failed to fetch')) {
        return 'No se pudo conectar con el servidor. Verifica que el servidor esté en ejecución y tu conexión a internet.'
      }
      return 'Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.'
    }

    const status = err.response?.status
    const data = err.response?.data

    // Obtener mensaje de error (puede estar en 'error', 'message' o 'error.message')
    const getErrorText = () => {
      if (data?.error) {
        // El backend puede devolver {error: "Invalid credentials"}
        if (typeof data.error === 'string') {
          return data.error
        }
        if (typeof data.error === 'object' && data.error?.message) {
          return data.error.message
        }
      }
      if (data?.message) {
        return data.message
      }
      return null
    }

    const errorText = getErrorText()

    // Traducir mensajes comunes del backend a español
    const translateError = (text: string): string => {
      const lowerText = text.toLowerCase()

      // Detectar errores de credenciales en cualquier formato
      if (lowerText.includes('invalid credentials') ||
          lowerText.includes('invalid credential') ||
          lowerText.includes('credenciales inv') ||
          lowerText.includes('incorrect password') ||
          lowerText.includes('wrong password') ||
          lowerText.includes('password incorrect') ||
          lowerText.includes('user not found') ||
          lowerText.includes('email not found')) {
        return 'Email o contraseña incorrectos. Por favor, verifica tus credenciales.'
      }

      const translations: Record<string, string> = {
        'Invalid credentials': 'Email o contraseña incorrectos. Por favor, verifica tus credenciales.',
        'Invalid credential': 'Email o contraseña incorrectos. Por favor, verifica tus credenciales.',
        'Credenciales inválidas': 'Email o contraseña incorrectos. Por favor, verifica tus credenciales.',
        'Credenciales incorrectas': 'Email o contraseña incorrectos. Por favor, verifica tus credenciales.',
        'Unauthorized': 'No autorizado. Por favor, verifica tus credenciales.',
      }
      return translations[text] || text
    }

    // Si hay texto de error, intentar traducirlo primero (sin importar el código HTTP)
    if (errorText && isLogin) {
      const translated = translateError(errorText)
      if (translated !== errorText) {
        return translated
      }
    }

    // Errores específicos por código de estado
    switch (status) {
      case 400:
        return errorText ? translateError(errorText) : 'Datos inválidos. Por favor, verifica la información ingresada.'
      case 401:
        if (isLogin) {
          // Para login, mensaje más específico sobre credenciales
          if (errorText) {
            const translated = translateError(errorText)
            // Si ya fue traducido, usarlo; si no, agregar contexto
            if (translated !== errorText) {
              return translated
            }
            return errorText.includes('credential') || errorText.includes('Invalid')
              ? 'Email o contraseña incorrectos. Por favor, verifica tus credenciales.'
              : errorText
          }
          return 'Email o contraseña incorrectos. Por favor, verifica tus credenciales.'
        }
        return errorText ? translateError(errorText) : 'No autorizado. Verifica tus credenciales.'
      case 403:
        return errorText || 'El registro se encuentra en fase Beta privada. Requiere un código de invitación válido.'
      case 404:
        return errorText || 'El servicio no está disponible. Por favor, contacta al administrador.'
      case 409:
        return errorText || 'Ya existe una cuenta con este email. Intenta iniciar sesión en su lugar.'
      case 422:
        // Errores de validación
        if (data?.errors) {
          const errorMessages = Object.values(data.errors).flat()
          return errorMessages.join(', ') || 'Datos inválidos. Por favor, verifica la información ingresada.'
        }
        return errorText || 'Datos inválidos. Por favor, verifica la información ingresada.'
      case 429:
        return 'Demasiados intentos. Por favor, espera unos momentos antes de intentar nuevamente.'
      case 500:
        return 'Error interno del servidor. Por favor, intenta nuevamente más tarde o contacta al administrador.'
      case 503:
        return 'El servicio no está disponible temporalmente. Por favor, intenta nuevamente más tarde.'
      default:
        return errorText ? translateError(errorText) : err.message || 'Ocurrió un error inesperado. Por favor, intenta nuevamente.'
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      if (!isLogin) {
        const validation = validateSignupInput({ email, password, name, inviteCode })
        if (!validation.valid) {
          setError(validation.error!)
          setLoading(false)
          return
        }
      }

      const endpoint = isLogin ? '/auth/login' : '/auth/signup'
      const requestData = isLogin
        ? { email, password }
        : {
            email,
            password,
            name,
            company: company || undefined,
            country: country || undefined,
            inviteCode: inviteCode.trim().toUpperCase(),
          }
      const { data } = await api.post(endpoint, requestData)

      // The response contains { user, tenant, tokens: { accessToken, refreshToken } }
      if (!data?.user || !data?.tokens) {
        throw new Error('Respuesta del servidor incompleta')
      }

      // Login válido sin tenant => error funcional, no de red.
      if (isLogin && !data?.tenant?.id) {
        throw new Error('Tu usuario no tiene un tenant asignado. Contacta a un administrador para vincular tu cuenta a una organización.')
      }

      // Extract user data including role
      const userData = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name || '',
        role: data.user.role || 'agent'  // Extract role from backend response
      }

      // Extract tenant ID from the response
      const tenantId = data.tenant.id

      // Store the refresh token in local storage
      if (data.tokens.refreshToken) {
        safeSetStorage('refreshToken', data.tokens.refreshToken)
      }

      // Limpiar datos del formulario solo después de login exitoso
      safeRemoveStorage(STORAGE_KEYS.LOGIN_EMAIL)
      safeRemoveStorage(STORAGE_KEYS.SIGNUP_NAME)
      safeRemoveStorage(STORAGE_KEYS.SIGNUP_INVITE_CODE)
      setPassword('') // Limpiar password por seguridad

      // Update auth store with token, tenant and user data
      login({
        token: data.tokens.accessToken,
        tenantId,
        tenant: data.tenant,
        user: userData
      })

      // Redirección post-login: respetar ruta previa si existe; si no, redirigir según vertical
      const from = (location.state as any)?.from?.pathname
      const targetPath = resolvePostLoginDestination(data.tenant?.businessType, from)
      navigate(targetPath, { replace: true })
    } catch (err: any) {
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
      // Los datos del formulario se mantienen gracias a localStorage
    } finally {
      setLoading(false)
    }
  }

  // Features para mostrar en el panel izquierdo
  const features = [
    { icon: Bot, title: 'Atención con IA', desc: 'Respuestas automáticas con criterio comercial y sin menús' },
    { icon: Zap, title: 'Inventario en Vivo', desc: 'Cotizaciones con existencias reales y reglas de tu negocio' },
    { icon: Users, title: 'Atención en Equipo', desc: 'Cola compartida para que varios miembros atiendan el mismo número' },
    { icon: TrendingUp, title: 'Métricas de Cierre', desc: 'Visibilidad completa de conversaciones y efectividad' },
  ]

  return (
    <div className="min-h-screen flex">
      {/* Panel Izquierdo - Branding (oculto en móvil) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#312E81] p-12 flex-col justify-between relative overflow-hidden">
        {/* Decoración de fondo */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-3 group focus:outline-hidden" title="Volver al inicio">
            <img
              src="/new/IridiaChat_Logo_Fondo_Oscuro_Transparente.png"
              alt="IridiaChat"
              className="h-10 sm:h-12 w-auto object-contain transition-opacity group-hover:opacity-90"
            />
            <span className="sr-only">IridiaChat - Volver al inicio</span>
            <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-white/20 text-white border border-white/30 rounded-full backdrop-blur-xs">
              BETA
            </span>
          </Link>
        </div>

        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Ventas por WhatsApp con
              <br />
              <span className="text-white/90">criterio comercial</span>
            </h1>
            <p className="mt-4 text-base text-white/80 max-w-md leading-relaxed">
              Atiende consultas 24/7, cotiza con existencias reales según las reglas de tu negocio y transfiere los pedidos listos a tu equipo de ventas.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, idx) => {
              const IconComp = feature.icon
              return (
                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/15">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white mb-2.5">
                    <IconComp className="w-4 h-4" />
                  </div>
                  <h3 className="font-semibold text-white text-sm">{feature.title}</h3>
                  <p className="text-xs text-white/75 mt-0.5 leading-relaxed">{feature.desc}</p>
                </div>
              )
            })}
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-4 text-white/70 text-xs">
          <span>© {new Date().getFullYear()} IridiaChat</span>
          <span>•</span>
          <Link to="/terminos" className="hover:text-white transition-colors underline">Términos</Link>
          <span>•</span>
          <Link to="/privacidad" className="hover:text-white transition-colors underline">Privacidad</Link>
        </div>
      </div>

      {/* Panel Derecho - Formulario */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Botón Volver al inicio */}
          <div className="mb-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors py-1.5 px-2.5 -ml-2.5 rounded-lg hover:bg-slate-100"
              title="Volver a la página principal"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Volver al inicio</span>
            </Link>
          </div>

          {/* Logo móvil */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2" title="Volver al inicio">
              <img
                src="/new/IridiaChat_Logo_Fondo_Claro_Transparente.png"
                alt="IridiaChat"
                className="h-9 sm:h-10 w-auto object-contain"
              />
              <span className="sr-only">IridiaChat - Volver al inicio</span>
              <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-800 border border-indigo-300 rounded-full">
                BETA
              </span>
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-100 text-indigo-800 border border-indigo-300 mb-3 shadow-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 animate-pulse" />
              Fase Beta Privada
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {isLogin ? 'Bienvenido de vuelta' : 'Acceso por Invitación'}
            </h2>
            <p className="mt-2 text-gray-600">
              {isLogin ? '¿Tienes un código de invitación?' : '¿Ya tienes cuenta activa?'}
              <Link
                to={buildTargetUrl(isLogin ? 'signup' : 'login')}
                className="font-semibold text-indigo-600 hover:text-indigo-700 ml-1 transition-colors"
              >
                {isLogin ? 'Regístrate aquí' : 'Inicia sesión'}
              </Link>
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={onSubmit} className="space-y-5">
            {!isLogin && (
              <>
                {queryPlan && (
                  <div className="bg-indigo-50 border border-indigo-200/90 rounded-xl px-3.5 py-2.5 flex items-center justify-between text-xs text-indigo-900 shadow-2xs">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600 shrink-0" />
                      <span>
                        Plan seleccionado:{' '}
                        <strong className="capitalize">{queryPlan === 'trial' ? 'Empieza ya · 7 Días Gratis' : queryPlan}</strong>
                      </span>
                    </div>
                    <span className="text-[10px] bg-indigo-600 text-white font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      7 Días Gratis
                    </span>
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200/90 rounded-xl p-3.5 flex items-start gap-3">
                  <Ticket className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-900 leading-relaxed">
                    <span className="font-bold">Registro por Invitación:</span> IridiaChat se encuentra en fase Beta privada con soporte prioritario. Requiere un código de invitación válido emitido para tu negocio.
                  </div>
                </div>
              </>
            )}

            {/* Campos adicionales para registro: Código de Invitación prioritario */}
            {!isLogin && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Código de invitación
                  </label>
                  <span className="text-[11px] font-semibold text-amber-800 bg-amber-100/80 border border-amber-200 px-2 py-0.5 rounded-full">
                    Requerido en Beta
                  </span>
                </div>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-600" />
                  <input
                    type="text"
                    required
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    className="w-full pl-10 pr-4 py-3 border border-amber-300 bg-amber-50/20 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400 uppercase font-mono tracking-wider font-semibold"
                    placeholder="Ingresa tu código de invitación"
                  />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 mt-1.5 text-[11px]">
                  <span className="text-gray-500">
                    Código emitido para activar tu negocio.
                  </span>
                  <a
                    href={getWhatsAppSalesUrl(
                      `Hola IridiaChat, deseo solicitar un código de invitación para probar la plataforma${
                        queryPlan ? ` con el plan ${queryPlan}` : ''
                      }.`
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 transition-colors underline"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                    <span>¿No tienes código? Solicítalo por WhatsApp</span>
                  </a>
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
                  placeholder="tu@email.com"
                />
              </div>
            </div>

            {/* Campos adicionales para registro */}
            {!isLogin && (
              <>
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nombre completo
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
                      placeholder="Juan Pérez"
                    />
                  </div>
                </div>

                {/* Empresa */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nombre de tu empresa <span className="text-gray-400">(opcional)</span>
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
                      placeholder="Mi Empresa S.A."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {/* País */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      País
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-10" />
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="w-full pl-10 pr-8 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 appearance-none bg-white cursor-pointer"
                      >
                        <option value="">Seleccionar</option>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.flag} {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Contraseña */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Contraseña
                </label>
                {isLogin && (
                  <Link
                    to="/forgot-password"
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
                  placeholder="••••••••"
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {!isLogin && (
                <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-600 text-xs font-bold">!</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setError('')}
                    className="text-red-400 hover:text-red-600"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Botón Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-semibold rounded-xl shadow-lg shadow-indigo-600/20 hover:shadow-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Procesando...</span>
                </>
              ) : (
                <>
                  <span>{isLogin ? 'Iniciar sesión' : 'Activar Cuenta Beta'}</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {/* Beneficios registro */}
            {!isLogin && (
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center mb-3">Al crear tu cuenta obtienes:</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {['7 días gratis', 'Sin tarjeta', 'Soporte 24/7'].map((item) => (
                    <span key={item} className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                      <CheckCircle2 className="w-3 h-3 text-[#25D366]" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </form>

          {/* Legal and compliance links */}
          <div className="mt-6 text-center text-xs text-gray-500 flex flex-wrap justify-center gap-3">
            <Link to="/privacidad" className="hover:text-emerald-700 underline transition-colors">
              Política de Privacidad
            </Link>
            <span>•</span>
            <Link to="/terminos" className="hover:text-emerald-700 underline transition-colors">
              Términos de Servicio
            </Link>
            <span>•</span>
            <Link to="/eliminacion-datos" className="hover:text-emerald-700 underline transition-colors">
              Eliminación de Datos
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
